/**
 * 空闲自动锁定 —— 修复原型缺陷 **B5**（无口令保护）。
 *
 * 触发条件（ARCH §5.4）：
 *  - 空闲 `autoLockMinutes` 分钟（默认 15）内无 `mousemove / keydown / pointerdown / touchstart`
 *  - 或用户手动「立即锁定」
 * 锁定后由路由守卫跳回 `/unlock`，发送与转账按钮禁用。
 */

import { useEffect, useRef } from 'react';
import { useIdentityStore } from '@/store/identity';
import { useSettingsStore } from '@/store/settings';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hook:autoLock');

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'pointerdown', 'touchstart', 'wheel'];

export interface UseAutoLockOptions {
  /** 是否启用（通常只在 unlocked 时启用）。 */
  enabled?: boolean;
  /** 覆盖时长（分钟）；缺省取 settings.autoLockMinutes。 */
  minutes?: number;
  /** 被锁定时回调（用于跳路由 / Toast）。 */
  onLock?: () => void;
}

/**
 * 启动空闲计时器。返回 `{ resetTimer, remainingMs }` 便于 UI 展示倒计时。
 */
export function useAutoLock(options: UseAutoLockOptions = {}): { resetTimer: () => void; remainingMs: () => number } {
  const { enabled = true, minutes, onLock } = options;
  const autoLockMinutes = useSettingsStore((s) => s.autoLockMinutes);
  const status = useIdentityStore((s) => s.status);
  const lock = useIdentityStore((s) => s.lock);

  const lastActivityRef = useRef<number>(Date.now());
  const timerRef = useRef<number | null>(null);
  const onLockRef = useRef(onLock);
  onLockRef.current = onLock;

  const effMinutes = minutes ?? autoLockMinutes;
  // 0 = 不锁定（关闭空闲自动锁定）
  const timeoutMs = effMinutes > 0 ? Math.max(1, effMinutes) * 60_000 : 0;

  const resetTimer = (): void => {
    lastActivityRef.current = Date.now();
  };

  useEffect(() => {
    if (!enabled || status !== 'unlocked' || timeoutMs <= 0) return;

    const doLock = (): void => {
      logger.info('空闲超时，自动锁定');
      lock();
      onLockRef.current?.();
    };

    const onActivity = (): void => {
      lastActivityRef.current = Date.now();
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') lastActivityRef.current = Date.now();
    });

    // 每 5s 检查一次是否超时（避免超长 setTimeout 在后台被节流）
    timerRef.current = window.setInterval(() => {
      if (Date.now() - lastActivityRef.current >= timeoutMs) doLock();
    }, 5_000);

    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, status, timeoutMs, lock]);

  return {
    resetTimer,
    remainingMs: () => Math.max(0, timeoutMs - (Date.now() - lastActivityRef.current)),
  };
}

export default useAutoLock;
